export type TablePaginationTranslations = {
  rowsPerPage: string;
  page: string;
  of: string;
  selected: string;
  totalOf: string;
  pageSize: string;
  row: string;
  item: string;
  goToFirstPage: string;
  goToPreviousPage: string;
  goToNextPage: string;
  goToLastPage: string;
  /** Infinite-scroll load-more row; replaces the page controls when `infiniteScroll` is set. */
  loadingMore: string;
};

export type TableFilterTranslations = {
  filters: string;
  resetAll: string;
  clearAll: string;
  showMore: (count: number) => string;
  showLess: string;
  noResultsFound: string;
  optionsLoadFailed: string;
  retry: string;
  retrying: string;
  yes: string;
  no: string;
  done: string;
  filterDescription: string;
  appliedFilters: string;
  noFiltersApplied: string;
  /** Switches the sidebar to the list of applied values, grouped by filter. */
  viewApplied: string;
  /** Switches the sidebar back from the applied list to every filter. */
  allFilters: string;
  excluded: string;
  /** Names the button that removes one applied value. */
  removeValue: (filterTitle: string, valueLabel: string) => string;
  useFilterOptions: string;
  filterCurrentlyApplied: (count: number) => string;
  search: (title: string) => string;
  /** Placeholder of the search that narrows the sidebar's own filter list by title. */
  searchFilters: string;
  noFiltersFound: string;
};

export type TableViewTranslations = {
  view: string;
  columns: string;
};

export type TableContextMenuTranslations = {
  openMenu: string;
  copyRowId: string;
  copiedToClipboard: string;
  failedToCopy: string;
};

type TableEmptyStateTranslations = {
  noResults: string;
};

export type TableRowTranslations = {
  /** Screen-reader description of a row that opens or picks something, read when it takes focus. */
  keyboardHint: string;
};

export type TableColumnHeaderTranslations = {
  asc: string;
  desc: string;
  resetSorting: string;
};

export type NoddiTableTranslations = {
  pagination?: Partial<TablePaginationTranslations>;
  filter?: Partial<TableFilterTranslations>;
  view?: Partial<TableViewTranslations>;
  contextMenu?: Partial<TableContextMenuTranslations>;
  emptyState?: Partial<TableEmptyStateTranslations>;
  columnHeader?: Partial<TableColumnHeaderTranslations>;
  row?: Partial<TableRowTranslations>;
  rowSelectionBottomBar?: Partial<RowSelectionActionBarTranslations>;
};

export type ResolvedNoddiTableTranslations = {
  pagination: TablePaginationTranslations;
  filter: TableFilterTranslations;
  view: TableViewTranslations;
  contextMenu: TableContextMenuTranslations;
  emptyState: TableEmptyStateTranslations;
  columnHeader: TableColumnHeaderTranslations;
  row: TableRowTranslations;
  rowSelectionBottomBar: RowSelectionActionBarTranslations;
};

export type RowSelectionActionBarTranslations = {
  itemsSelected: (count: number) => string;
  clearSelection: string;
  actions: string;
  selectAllMatching: (totalMatchingCount: number) => string;
};

const defaultPaginationTranslations: TablePaginationTranslations = {
  rowsPerPage: 'Page size',
  page: 'Page',
  of: 'of',
  selected: 'selected',
  totalOf: 'Total of',
  pageSize: 'Page size',
  row: 'row(s)',
  item: 'item(s)',
  goToFirstPage: 'Go to first page',
  goToPreviousPage: 'Go to previous page',
  goToNextPage: 'Go to next page',
  goToLastPage: 'Go to last page',
  loadingMore: 'Loading more…'
};

const defaultFilterTranslations: TableFilterTranslations = {
  filters: 'Filters',
  resetAll: 'Reset all',
  clearAll: 'Clear all',
  showMore: (count: number) => `Show ${count} more`,
  showLess: 'Show less',
  noResultsFound: 'No results found',
  optionsLoadFailed: 'Could not load options',
  retry: 'Try again',
  retrying: 'Retrying…',
  yes: 'Yes',
  no: 'No',
  done: 'Done',
  filterDescription: 'Use filters to narrow down your results',
  appliedFilters: 'Applied filters',
  noFiltersApplied: 'No filters are currently applied',
  viewApplied: 'View applied',
  allFilters: 'All filters',
  excluded: 'Excluded',
  removeValue: (filterTitle: string, valueLabel: string) => `Remove ${filterTitle}: ${valueLabel}`,
  useFilterOptions: 'Use the filter options to narrow down your results',
  filterCurrentlyApplied: (count: number) => `${count} ${count === 1 ? 'filter' : 'filters'} currently applied`,
  search: (title: string) => `Search ${title.toLowerCase()}...`,
  searchFilters: 'Search filters...',
  noFiltersFound: 'No filters found'
};

const defaultViewTranslations: TableViewTranslations = {
  view: 'View',
  columns: 'Columns'
};

const defaultContextMenuTranslations: TableContextMenuTranslations = {
  openMenu: 'Open menu',
  copyRowId: 'Copy row id',
  copiedToClipboard: 'Copied to clipboard',
  failedToCopy: 'Failed to copy to clipboard'
};

const defaultEmptyStateTranslations: TableEmptyStateTranslations = {
  noResults: 'No results'
};

const defaultColumnHeaderTranslations: TableColumnHeaderTranslations = {
  asc: 'Asc',
  desc: 'Desc',
  resetSorting: 'Reset sorting'
};

const defaultRowTranslations: TableRowTranslations = {
  keyboardHint: 'Press Enter to open'
};

const defaultActionBarTranslations: RowSelectionActionBarTranslations = {
  itemsSelected: (count: number) => `${count} ${count === 1 ? 'row' : 'rows'} selected`,
  clearSelection: 'Clear selection',
  actions: 'Actions',
  selectAllMatching: (totalMatchingCount: number) => `Select all ${totalMatchingCount} matching rows`
};

export const resolveTablePaginationTranslations = (
  overrides?: Partial<TablePaginationTranslations>
): TablePaginationTranslations => {
  return { ...defaultPaginationTranslations, ...overrides };
};

export const resolveTableFilterTranslations = (
  overrides?: Partial<TableFilterTranslations>
): TableFilterTranslations => {
  return { ...defaultFilterTranslations, ...overrides };
};

export const resolveTableViewTranslations = (overrides?: Partial<TableViewTranslations>): TableViewTranslations => {
  return { ...defaultViewTranslations, ...overrides };
};

export const resolveTableContextMenuTranslations = (
  overrides?: Partial<TableContextMenuTranslations>
): TableContextMenuTranslations => {
  return { ...defaultContextMenuTranslations, ...overrides };
};

const resolveTableEmptyStateTranslations = (
  overrides?: Partial<TableEmptyStateTranslations>
): TableEmptyStateTranslations => {
  return { ...defaultEmptyStateTranslations, ...overrides };
};

export const resolveTableColumnHeaderTranslations = (
  overrides?: Partial<TableColumnHeaderTranslations>
): TableColumnHeaderTranslations => {
  return { ...defaultColumnHeaderTranslations, ...overrides };
};

export const resolveTableRowTranslations = (overrides?: Partial<TableRowTranslations>): TableRowTranslations => {
  return { ...defaultRowTranslations, ...overrides };
};

const resolveRowSelectionActionBarTranslations = (
  overrides?: Partial<RowSelectionActionBarTranslations>
): RowSelectionActionBarTranslations => {
  return { ...defaultActionBarTranslations, ...overrides };
};

export const resolveNoddiTableTranslations = (overrides?: NoddiTableTranslations): ResolvedNoddiTableTranslations => {
  return {
    pagination: resolveTablePaginationTranslations(overrides?.pagination),
    filter: resolveTableFilterTranslations(overrides?.filter),
    view: resolveTableViewTranslations(overrides?.view),
    contextMenu: resolveTableContextMenuTranslations(overrides?.contextMenu),
    emptyState: resolveTableEmptyStateTranslations(overrides?.emptyState),
    columnHeader: resolveTableColumnHeaderTranslations(overrides?.columnHeader),
    row: resolveTableRowTranslations(overrides?.row),
    rowSelectionBottomBar: resolveRowSelectionActionBarTranslations(overrides?.rowSelectionBottomBar)
  };
};
